import React from "react";
import { TableCell, Stack, TableSortLabel } from "@mui/material";
import { incomeTypes } from "./calculations";
import CountryField from "./CountryField";
import CommonTable, { ReadOnlyCell } from "../../utils/CommonTable";
import {
  formatAmount,
  formatScheduleName,
} from "../../../utils/hooks/dataHandler";
import GroupedSelect from "../../utils/GroupedSelect";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { TextFieldForName } from "../../utils/TextField/TextField";
import TaxField from "../../utils/TextField/TaxField";
import { YearPicker } from "../../utils/Form/YearPicker";

const CurrentYearIncomeTable = ({
  data,
  onLockRow,
  onUnlockRow,
  onChange,
  onRemove,
  totals,
  isPriorYear,
  onSortYaEarned,
  sortDirection,
  canEdit = true,
}) => {
  const getTableColumns = () => [
    ...(isPriorYear
      ? []
      : [
          {
            key: "description",
            label: "Description",
            headerAlign: "left",
            colAlign: "left",
            render: (row) => (
              <ReadOnlyCell
                value={
                  formatScheduleName(
                    row.source?.sch_name,
                    row.source?.sch_no
                  ) || ""
                }
                width='180px'
                textAlign='left'
              />
            ),
          },
        ]),
    ...(isPriorYear
      ? [
          {
            key: "yaEarned",
            label: onSortYaEarned ? (
              <TableSortLabel
                active={Boolean(sortDirection)}
                direction={sortDirection || "asc"}
                onClick={() =>
                  onSortYaEarned(sortDirection === "asc" ? "desc" : "asc")
                }
                aria-label='Sort prior year income by YA earned'
              >
                YA Earned
              </TableSortLabel>
            ) : (
              "YA Earned"
            ),
            headerAlign: "center",
            colAlign: "center",
            width: "120px",
            render: (row) => (
              <YearPicker
                year={row.yaEarned || ""}
                onChange={(year, ref) => {
                  onChange(year, {
                    rowId: row.rowId,
                    field: "yaEarned",
                  });
                }}
                disabled={row.isLocked}
                allowManualInput
                canEdit={canEdit}
              />
            ),
          },
        ]
      : []),
    {
      key: "incomeType",
      label: "Income Type",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => (
        <GroupedSelect
          value={row.incomeType || ""}
          options={incomeTypes}
          onChange={(e) =>
            onChange(e.target.value, {
              rowId: row.rowId,
              field: "incomeType",
            })
          }
          disabled={row.isLocked}
          enableSearch
          canEdit={canEdit}
        />
      ),
    },
    {
      key: "country",
      label: "Country",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => (
        <CountryField
          key={row.rowId}
          country={row.country}
          onChange={(value) =>
            onChange(value, { rowId: row.rowId, field: "country" })
          }
          disabled={row.isLocked}
          canEdit={canEdit}
        />
      ),
    },
    {
      key: "amount",
      label: "Available Income",
      headerAlign: "right",
      colAlign: "right",
      width: "140px",
      render: (row) => (
        <>
          {row.isPriorYear ? (
            <TaxField
              value={row.amount}
              onChange={onChange}
              reference={{
                rowId: row.rowId,
                field: "amount",
                fieldType: "numeric",
              }}
              disabled={row.isLocked}
              canEdit={canEdit}
            />
          ) : (
            <ReadOnlyCell
              value={formatAmount(row.amount)}
              width='100%'
              textAlign='right'
            />
          )}
        </>
      ),
    },
    {
      key: "allowableExpenses",
      label: "Allowable Expenses",
      headerAlign: "right",
      colAlign: "right",
      render: (row) => (
        <>
          {row.isPriorYear ? (
            <TaxField
              value={row.allowableExpenses}
              onChange={onChange}
              reference={{
                rowId: row.rowId,
                field: "allowableExpenses",
                fieldType: "numeric",
              }}
              disabled={row.isLocked}
              canEdit={canEdit}
            />
          ) : (
            <ReadOnlyCell
              value={formatAmount(row.allowableExpenses)}
              width='100%'
              textAlign='right'
            />
          )}
        </>
      ),
    },
    {
      key: "foreignTaxSuffered",
      label: "Foreign Tax Suffered",
      headerAlign: "right",
      colAlign: "right",
      width: "160px",
      render: (row) => (
        <>
          {row.isPriorYear ? (
            <TaxField
              value={row.foreignTaxSuffered}
              onChange={onChange}
              reference={{
                rowId: row.rowId,
                field: "foreignTaxSuffered",
                fieldType: "numeric",
              }}
              decimalScale={2}
              disabled={row.isLocked}
              canEdit={canEdit}
            />
          ) : (
            <ReadOnlyCell
              value={formatAmount(
                row.foreignTaxSuffered,
                true,
                false,
                false,
                true
              )}
              width='100%'
              textAlign='right'
            />
          )}
        </>
      ),
    },
    {
      key: "reference",
      label: "Reference",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => (
        <TextFieldForName
          value={row?.reference || ""}
          onChange={onChange}
          reference={{
            rowId: row.rowId,
            field: "reference",
          }}
          canEdit={canEdit}
          viewOnlyAlign='center'
        />
      ),
    },
    canEdit
      ? {
          key: "actions",
          label: "Actions",
          headerAlign: "center",
          colAlign: "center",
          responsiveWidth: isPriorYear ? "112px" : "64px",
          render: (row) => (
            <Stack
              direction='row'
              spacing={1}
              justifyContent='center'
              alignItems='center'
              sx={{ p: 0.5, "& .MuiIconButton-root": { flexShrink: 0 } }}
            >
              {row.isLocked ? (
                <IconButtonWithTooltip
                  icon={LockOpenIcon}
                  tooltipTitle='Unlock the row'
                  size='small'
                  onClick={() => onUnlockRow(row.rowId)}
                  color='success'
                />
              ) : (
                <IconButtonWithTooltip
                  icon={LockIcon}
                  tooltipTitle='Lock the row'
                  onClick={() => onLockRow(row.rowId)}
                  size='small'
                  disabled={!row.incomeType || (isPriorYear && !row.yaEarned)}
                  color='warning'
                />
              )}
              {onRemove && (
                <IconButtonWithTooltip
                  icon={DeleteOutlineIcon}
                  tooltipTitle='Delete Row'
                  onClick={() => onRemove(row.rowId)}
                  color='error'
                  size='small'
                  disabled={row.isLocked}
                />
              )}
            </Stack>
          ),
        }
      : [],
  ];

  return (
    <CommonTable
      rows={data}
      columns={getTableColumns()}
      noRowsHint='No foreign income data available yet.'
    >
      <TableCell colSpan={2} />
      <TableCell align='right' sx={{ fontWeight: "bold" }}>
        Total
      </TableCell>
      <TableCell align='right' sx={{ fontWeight: "bold" }}>
        {formatAmount(totals?.amount || 0)}
      </TableCell>
      <TableCell align='right' sx={{ fontWeight: "bold", width: "160px" }}>
        {formatAmount(totals?.allowableExpenses || 0)}
      </TableCell>
      <TableCell align='right' sx={{ fontWeight: "bold" }}>
        {formatAmount(totals?.foreignTaxSuffered || 0, false)}
      </TableCell>
      <TableCell colSpan={3} />
    </CommonTable>
  );
};

export default CurrentYearIncomeTable;
